import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { MeetingActionTab } from "@/pages/workflows/edit/actions/meeting/MeetingActionTab.tsx";
import {
  buildMeetingSchema,
  MeetingFormValues,
} from "@/pages/workflows/edit/actions/meeting/workflow-meeting.schema.ts";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  isMeetingConferenceAvailable,
  meetingDirectionToOffset,
  meetingEntityTypeForField,
  meetingOffsetToDirection,
} from "@/pages/workflows/edit/actions/workflow-meeting.helpers.ts";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
} from "@/pages/workflows/workflow-hook.tsx";
import {
  getUnavailableRuleContextsForSubject,
  useRuleConditionTypes,
} from "@/queries/use-rules.query.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import {
  useWorkflowMeetingOrganizerCalendars,
  useWorkflowUpdateBlockActionMeeting,
} from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { EmployeeFilterDefault, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiEventVisibility } from "@/types/event.types.ts";
import { Rule, RuleApplySignType, RuleConditionContext } from "@/types/rule.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { ApiWorkflowBlockActionMeeting } from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowMeetingDateReference,
  ApiWorkflowTriggerType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  SelectItem,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface WorkflowEditActionMeetingModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionMeeting: ApiWorkflowBlockActionMeeting;
}

export const WorkflowEditActionMeetingModal = ({
  workflowVersion,
  block,
  actionMeeting,
  ...props
}: WorkflowEditActionMeetingModalProps) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { close } = useModals();
  const [activeTab, setActiveTab] = useState(0);

  const workflowTriggerType = workflowVersion.trigger_recurring_rule
    ? ApiWorkflowTriggerType.recurring
    : workflowVersion.trigger_schedule_rule
      ? ApiWorkflowTriggerType.scheduled
      : workflowVersion.trigger_event
        ? ApiWorkflowTriggerType.event
        : ApiWorkflowTriggerType.manual;

  const eventType = workflowVersion.trigger_event?.event_type;
  const subjectUnavailableContexts = useMemo(
    () => getUnavailableRuleContextsForSubject(eventType),
    [eventType]
  );

  const attendeeConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowMeetingAttendees,
    workflowTriggerType,
    subjectUnavailableContexts
  );
  const attendeeConditionTypeInfoById = new Map(
    attendeeConditionTypes.conditionTypeInfos.map((conditionType) => [
      conditionType.condition_type,
      conditionType,
    ])
  );
  const isSupportedAttendeeRule = (rule: Rule) => {
    const conditionType = attendeeConditionTypeInfoById.get(rule.condition_type);

    return Boolean(
      conditionType &&
      (conditionType.context !== RuleConditionContext.affected_employee ||
        rule.sign === RuleApplySignType.include)
    );
  };

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<MeetingFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(buildMeetingSchema()),
    resetOptions: { keepDirtyValues: true },
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      meeting_title: actionMeeting.title ?? "",
      organizer_id: actionMeeting.organizer?.id ?? null,
      calendar_id: actionMeeting.calendar?.id ?? null,
      start_time: actionMeeting.start_time || "09:00:00",
      duration_minutes: actionMeeting.duration_minutes || 30,
      attendee_rule_groups: (actionMeeting.attendee_rule_groups ?? [])
        .map((group) => ({ ...group, rules: group.rules.filter(isSupportedAttendeeRule) }))
        .filter((group) => group.rules.length > 0),
      external_attendee_emails: actionMeeting.external_attendee_emails ?? [],
      description: actionMeeting.description,
      location: actionMeeting.location,
      visibility: actionMeeting.visibility ?? ApiEventVisibility.public,
      is_conference: actionMeeting.is_conference ?? false,
      meeting_date_reference: actionMeeting.meeting_date.reference,
      meeting_date_entity_field: actionMeeting.meeting_date.entity_field,
      meeting_date_direction: meetingOffsetToDirection(actionMeeting.meeting_date.offset_days),
      meeting_date_offset: Math.abs(actionMeeting.meeting_date.offset_days),
      meeting_date_day_type: actionMeeting.meeting_date.day_type,
    },
  });

  const organizerId = useWatch({ control, name: "organizer_id" });
  const calendarId = useWatch({ control, name: "calendar_id" });

  const [organizerOption, setOrganizerOption] = useState<SelectItem | null>(
    actionMeeting.organizer
      ? {
          id: actionMeeting.organizer.id,
          title: renderEmployeeName(actionMeeting.organizer),
          avatarUrl: getEmployeeAvatarUrl(actionMeeting.organizer, "sm"),
        }
      : null
  );

  const employeesSearch = useEmployeeSearchInline(true, EmployeeFilterDefault);

  const organizerOptions = useMemo(() => {
    const searchOptions = employeesSearch.options;
    if (organizerOption && !searchOptions.some((option) => option.id === organizerOption.id)) {
      return [organizerOption, ...searchOptions];
    }
    return searchOptions;
  }, [employeesSearch.options, organizerOption]);

  const { data: calendars } = useWorkflowMeetingOrganizerCalendars(
    workflowVersion.workflow_id,
    organizerId ?? undefined
  );

  const selectedCalendar = useMemo(
    () => calendars?.find((calendar) => calendar.id === calendarId),
    [calendars, calendarId]
  );
  const conferenceAvailable = isMeetingConferenceAvailable(selectedCalendar);

  const handleOrganizerChange = (newValue: number | null) => {
    setValue("organizer_id", newValue);
    setOrganizerOption(organizerOptions.find((option) => option.id === newValue) ?? null);
    setValue("calendar_id", null);
    setValue("is_conference", false);
  };

  const handleCalendarChange = (newValue: number | null) => {
    setValue("calendar_id", newValue);
    const nextCalendar = calendars?.find((calendar) => calendar.id === newValue);
    if (!isMeetingConferenceAvailable(nextCalendar)) {
      setValue("is_conference", false);
    }
  };

  const updateBlockAction = useWorkflowUpdateBlockActionMeeting(control);

  const handleSave = handleSubmit(async (data) => {
    try {
      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: {
            organizer_id: data.organizer_id,
            calendar_id: data.calendar_id,
            title: data.meeting_title,
            description: data.description,
            location: data.location,
            visibility: data.visibility,
            attendee_rule_groups: data.attendee_rule_groups
              .filter((group) => group.rules.length > 0)
              .map((group) => ({
                rules: group.rules.map((rule) => ({
                  condition_type: rule.condition_type,
                  sign: rule.sign,
                  condition_ids: rule.condition_ids,
                  value_ids: rule.value_ids,
                  date_condition: rule.date_condition,
                })),
              })),
            external_attendee_emails: data.external_attendee_emails,
            start_time: data.start_time,
            duration_minutes: data.duration_minutes,
            is_conference: conferenceAvailable ? data.is_conference : false,
            meeting_date:
              data.meeting_date_reference === ApiWorkflowMeetingDateReference.entity_date &&
              data.meeting_date_entity_field !== null
                ? {
                    reference: ApiWorkflowMeetingDateReference.entity_date,
                    entity_type: meetingEntityTypeForField(data.meeting_date_entity_field),
                    entity_field: data.meeting_date_entity_field,
                    offset_days: meetingDirectionToOffset(
                      data.meeting_date_direction,
                      data.meeting_date_offset
                    ),
                    day_type: data.meeting_date_day_type,
                  }
                : {
                    reference: ApiWorkflowMeetingDateReference.block_execution,
                    entity_type: null,
                    entity_field: null,
                    offset_days: data.meeting_date_offset,
                    day_type: data.meeting_date_day_type,
                  },
          },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.meeting,
      });

      close(props.id);
    } catch {}
  });

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_meeting")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={handleSave}
            isLoading={updateBlockAction.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("workflow:action_type_meeting")}>
            <MeetingActionTab
              control={control}
              register={register}
              errors={errors}
              setValue={setValue}
              organizerOptions={organizerOptions}
              onOrganizerSearch={employeesSearch.search}
              onOrganizerChange={handleOrganizerChange}
              calendars={calendars}
              organizerId={organizerId}
              onCalendarChange={handleCalendarChange}
              conferenceAvailable={conferenceAvailable}
              eventType={eventType}
              attendeeConditionTypes={attendeeConditionTypes}
            />
          </TabItem>
          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("title")}
                  error={errors.title?.message}
                />
              </Stack>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />
              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
